/**
 * TrainingSession — split-screen training experience.
 *
 *   LEFT  — video player. Tracks playback time and updates progress in
 *           the backend every 5 seconds (so a refresh doesn't lose state).
 *           When the user has watched the whole video, the "Take quiz"
 *           button unlocks at the bottom.
 *   RIGHT — tutor chat panel. Sends questions to /cima/training/chat
 *           which calls Bedrock Claude with the module context as a
 *           system prompt. Tutor will NEVER reveal quiz answers.
 *
 * QuizModal opens overlaid when the user clicks "Take quiz". Score is
 * shown inline with per-question explanations after submission.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  TrainingAssignment, TrainingQuestion, AttemptResult,
  startAssignment, saveVideoProgress, startAttempt, submitAttempt,
  chatWithTutor, openCertificate,
} from '../../api/training';
import { useStaffProfile } from '../../hooks/useStaffProfile';
import { MarkdownText } from '../../components/MarkdownText';

interface ChatMsg { role: 'user' | 'assistant'; content: string; }

export function TrainingSession({ assignment, onExit }: {
  assignment: TrainingAssignment;
  onExit: () => Promise<void>;
}) {
  const me = useStaffProfile();
  // If the admin hasn't uploaded a video for this module yet, there's nothing
  // to watch — so we treat video as implicitly "100%" so the quiz unlocks
  // straight away. The backend gate still enforces the rule; see the useEffect
  // below that pushes this value up-stream.
  const hasVideo = !!assignment.videoUrl;
  const [progress, setProgress] = useState(() =>
    hasVideo ? (assignment.videoProgressPercent || 0) : 100,
  );
  const [showQuiz, setShowQuiz] = useState(false);
  const [completed, setCompleted] = useState(assignment.status === 'COMPLETED');

  // ── Mark the assignment as started on first open ──
  useEffect(() => {
    if (assignment.status === 'NOT_STARTED') {
      startAssignment(assignment.id).catch(() => {});
    }
    // No video? Persist 100% progress so the backend unlocks the quiz attempt.
    if (!hasVideo && (assignment.videoProgressPercent || 0) < 100) {
      saveVideoProgress(assignment.id, 100).catch(() => {});
    }
  }, [assignment.id, assignment.status, hasVideo, assignment.videoProgressPercent]);

  // ── Tutor chat state ──
  const greeting = hasVideo
    ? `Hi ${me?.firstName || 'there'}! I'm your training tutor for **${assignment.moduleTitle}**. Watch the video on the left and let me know if anything's unclear. When you're done, hit the quiz button to test what you've learned.`
    : `Hi ${me?.firstName || 'there'}! I'm your training tutor for **${assignment.moduleTitle}**. Your IC hasn't uploaded a video for this module yet, so you can skip straight to the quiz. Ask me anything about the topic before you try it.`;
  const [messages, setMessages] = useState<ChatMsg[]>([
    { role: 'assistant', content: greeting },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const reply = await chatWithTutor(assignment.id, text, messages);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (err: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: `(error: ${err.message})` }]);
    } finally { setSending(false); }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      zIndex: 200, /* must sit ABOVE the navbar (z-index:100) */
    }}>
      {/* Top bar */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 22px', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', flexShrink: 0,
      }}>
        <button onClick={onExit} style={{
          background: 'transparent', border: '1px solid var(--border-md)',
          borderRadius: 6, padding: '5px 12px', color: 'var(--fg)',
          fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
        }}>← Exit training</button>
        <div style={{ textAlign: 'center', flex: 1, padding: '0 20px' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: 'var(--fg)' }}>
            {assignment.moduleTitle}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            Video {progress}% · Pass {assignment.passingScore}% · Due {new Date(assignment.deadlineAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
          </div>
        </div>
        <button
          onClick={() => progress >= 100 && setShowQuiz(true)}
          disabled={progress < 100 && !completed}
          title={progress < 100 ? `Watch the full video first (${progress}% watched)` : 'Take the quiz'}
          style={{
            background: completed ? 'rgba(74,222,128,0.15)'
              : progress >= 100 ? 'var(--primary)'
              : 'var(--faint)',
            color: completed ? '#4ade80'
              : progress >= 100 ? 'var(--on-primary)'
              : 'var(--muted)',
            border: completed ? '1px solid rgba(74,222,128,0.30)' : 'none',
            borderRadius: 8, padding: '8px 16px',
            fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
            cursor: progress >= 100 || completed ? 'pointer' : 'not-allowed',
            opacity: progress < 100 && !completed ? 0.7 : 1,
            display: 'inline-flex', alignItems: 'center', gap: 7,
          }}
        >
          {completed ? (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>Retake quiz</>
          ) : progress >= 100 ? (
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M9 15l2 2 4-4" /></svg>Take quiz</>
          ) : (
            <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>Watch video first · {progress}%</>
          )}
        </button>
      </div>

      {/* Body — split-screen */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* LEFT — video */}
        <div style={{ flex: '1 1 60%', display: 'flex', flexDirection: 'column', background: '#0c0714' }}>
          <VideoPlayer
            assignment={assignment}
            initialProgress={progress}
            onProgress={(pct) => setProgress(pct)}
            onSkip={() => setShowQuiz(true)}
          />
        </div>

        {/* RIGHT — tutor chat OR inline quiz panel */}
        <div style={{
          flex: '1 1 40%', minWidth: 320,
          display: 'flex', flexDirection: 'column',
          background: 'var(--surface)', borderLeft: '1px solid var(--border)',
          position: 'relative', overflow: 'hidden',
        }}>
          {showQuiz ? (
            <QuizPanel
              assignmentId={assignment.id}
              passingScore={assignment.passingScore || 70}
              onClose={() => setShowQuiz(false)}
              // "Claim your badge" on the celebration screen should return
              // the user to the My Training list — not leave them parked in
              // a completed module. onExit closes the whole session.
              onPassExit={async () => { await onExit(); }}
              onCompleted={async (passed) => {
                if (passed) setCompleted(true);
              }}
            />
          ) : (
            <>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 18px', borderBottom: '1px solid var(--border)',
                background: 'var(--card)', flexShrink: 0,
              }}>
                <span style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--primary)', color: '#fff' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10 12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.5 2.5 6 2.5s6-1.5 6-2.5v-5" /></svg>
                </span>
                <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)' }}>Training Tutor</span>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {messages.map((m, i) => (
                  m.role === 'user' ? (
                    <div key={i} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div style={{
                        maxWidth: '82%', padding: '10px 14px', borderRadius: 14,
                        background: 'color-mix(in srgb, var(--fg) 8%, transparent)', color: 'var(--fg)',
                        fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.55,
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      }}>
                        {m.content}
                      </div>
                    </div>
                  ) : (
                    <div key={i} style={{
                      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
                      padding: '13px 16px', fontFamily: 'var(--font-body)',
                      fontSize: 13.5, lineHeight: 1.6, color: 'var(--fg)',
                    }}>
                      <MarkdownText>{m.content}</MarkdownText>
                    </div>
                  )
                ))}
                {sending && (
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <div style={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 12, padding: '11px 15px',
                      display: 'flex', gap: 4,
                    }}>
                      {[0,1,2].map(i => <span key={i} style={{
                        width: 5, height: 5, borderRadius: '50%', background: 'var(--primary)',
                        animation: `blink 1.2s ease-in-out ${i * 0.2}s infinite`,
                      }} />)}
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div style={{
                padding: 12, borderTop: '1px solid var(--border)', flexShrink: 0,
                background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {/* Prominent quiz CTA once the video's done (or there's no video). */}
                {progress >= 100 && !completed && (
                  <button
                    onClick={() => setShowQuiz(true)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%',
                      background: 'var(--primary)', color: 'var(--on-primary)', border: 'none', borderRadius: 12,
                      padding: '13px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: '0 8px 20px -8px color-mix(in srgb, var(--primary) 65%, transparent)',
                    }}
                  >
                    I&rsquo;m ready — start the quiz
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                  </button>
                )}
                <div className="training-composer" style={{
                  display: 'flex', alignItems: 'flex-end', gap: 6,
                  background: 'var(--card)', border: '1px solid var(--border-md)',
                  borderRadius: 12, padding: '6px 6px 6px 14px',
                }}>
                  <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Ask the tutor anything about this module…"
                    rows={1}
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      color: 'var(--fg)', fontSize: 13, resize: 'none', fontFamily: 'inherit',
                      lineHeight: 1.5, maxHeight: 100, padding: '6px 0',
                    }}
                  />
                  <button
                    onClick={send} disabled={!input.trim() || sending} aria-label="Send"
                    style={{
                      background: input.trim() && !sending ? 'var(--primary)' : 'var(--faint)',
                      color: input.trim() && !sending ? 'var(--on-primary)' : 'var(--muted)',
                      border: 'none', borderRadius: 9, width: 34, height: 34, flexShrink: 0,
                      cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Video player ──────────────────────────────────────────────────────

function VideoPlayer({ assignment, initialProgress, onProgress, onSkip }: {
  assignment: TrainingAssignment;
  initialProgress: number;
  onProgress: (pct: number) => void;
  onSkip?: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const lastSavedRef = useRef(initialProgress);
  // True when the <video> element fired an `error` event — usually means
  // the stored video_url returns 404 / SPA fallback HTML / wrong content
  // type. Falling back to the empty state is much friendlier than leaving
  // a black box with no controls. We also auto-credit the user with 100%
  // progress so the quiz still unlocks (same behaviour as "no video").
  const [loadFailed, setLoadFailed] = useState(false);

  // Throttle backend updates: save at most every 5 seconds, plus on pause.
  const saveProgress = useCallback(async (pct: number) => {
    if (pct <= lastSavedRef.current) return;
    lastSavedRef.current = pct;
    try { await saveVideoProgress(assignment.id, pct); }
    catch { /* non-fatal */ }
  }, [assignment.id]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    let ticker: any;
    const onTimeUpdate = () => {
      if (!v.duration) return;
      const pct = Math.min(100, Math.floor((v.currentTime / v.duration) * 100));
      onProgress(pct);
    };
    const onPause = () => {
      const pct = v.duration ? Math.floor((v.currentTime / v.duration) * 100) : 0;
      saveProgress(pct);
    };
    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', () => { onProgress(100); saveProgress(100); });
    // Periodic background save every 5s
    ticker = setInterval(() => {
      if (!v.duration || v.paused) return;
      const pct = Math.floor((v.currentTime / v.duration) * 100);
      saveProgress(pct);
    }, 5000);
    return () => {
      clearInterval(ticker);
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('pause', onPause);
    };
  }, [saveProgress, onProgress]);

  // Fall back to the empty state when there's either no URL OR the URL
  // failed to load (404, wrong content-type, SPA fallback HTML, etc.).
  // This rescues users from a black-box player that just sits there.
  if (!assignment.videoUrl || loadFailed) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, textAlign: 'center' }}>
        <div style={{ maxWidth: 380, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <span style={{ width: 84, height: 84, borderRadius: 22, display: 'grid', placeItems: 'center', background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.28)', color: '#9DC0FF', marginBottom: 18 }}>
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8z" /><rect x="2" y="6" width="14" height="12" rx="2" /></svg>
          </span>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: '#f4eefb' }}>
            {loadFailed ? "This video can't be played" : 'No video for this module yet'}
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(244,238,251,0.6)', margin: '7px 0 20px' }}>
            {loadFailed
              ? 'It was likely uploaded in an unsupported format. Ask your IC to re-upload it as MP4 (H.264) or WebM — you can still take the quiz.'
              : "Your IC hasn't uploaded a training video. You can go straight to the quiz whenever you're ready."}
          </p>
          {onSkip && (
            <button onClick={onSkip} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.16)', color: '#fff',
              borderRadius: 11, padding: '11px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              Skip to the quiz
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      src={assignment.videoUrl}
      controls
      autoPlay={false}
      onError={() => {
        // Browser couldn't load the resource — flip to the empty state and
        // credit the user with 100% video progress so the quiz unlocks
        // (matches the "no video" branch above).
        setLoadFailed(true);
        onProgress(100);
        saveProgress(100);
      }}
      style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
    />
  );
}

// ══════════════════════════════════════════════════════════════════════════
// QuizPanel — one question at a time, rendered INLINE in the right column
// (no fixed overlay). The video stays visible on the left so the user can
// reference it while answering. Celebration screen renders in-place.
// ══════════════════════════════════════════════════════════════════════════

function QuizPanel({ assignmentId, passingScore, onClose, onPassExit, onCompleted }: {
  assignmentId: number;
  passingScore: number;
  /** Back to the tutor chat (used pre-result + on "Try again" after fail). */
  onClose: () => void;
  /** After a PASS, what to run when the user clicks "Claim your badge" —
   *  typically exits the whole TrainingSession back to the My Training list,
   *  so the user doesn't get parked in a completed module. */
  onPassExit: () => Promise<void>;
  onCompleted: (passed: boolean) => Promise<void>;
}) {
  const [attemptId, setAttemptId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [qi, setQi] = useState(0);             // current question index
  const [answers, setAnswers] = useState<Record<number, any>>({});
  const [result, setResult] = useState<AttemptResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Explicit load state — prevents the UI from sitting forever on "Loading
  // questions..." if startAttempt throws (previous bug) or returns an empty
  // array (module with no questions). Tri-state: 'loading' / 'ready' / 'error'.
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0); // bump to retry
  const startedAt = useRef(Date.now());

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    setError('');
    startAttempt(assignmentId)
      .then(({ attemptId: aid, questions: qs }) => {
        if (cancelled) return;
        setAttemptId(aid);
        setQuestions(qs);
        startedAt.current = Date.now();
        setLoadState('ready');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err.message || 'Could not start the attempt');
        setLoadState('error');
      });
    return () => { cancelled = true; };
  }, [assignmentId, loadAttempt]);

  const current = questions[qi];
  const total = questions.length;
  const isLast = qi >= total - 1;
  const progressPct = total > 0 ? Math.round((qi / total) * 100) : 0;

  function nextQuestion() {
    if (!current) return;
    const given = answers[current.id];
    if (given === undefined || given === null || (Array.isArray(given) && !given.length) || given === '') {
      setError('Pick an answer first');
      return;
    }
    setError('');
    if (isLast) {
      submitAll();
    } else {
      setQi(qi + 1);
    }
  }

  async function submitAll() {
    if (!attemptId) return;
    setSubmitting(true);
    setError('');
    try {
      const seconds = Math.floor((Date.now() - startedAt.current) / 1000);
      const responses = questions.map(q => ({
        questionId: q.id,
        selectedAnswers: answers[q.id] ?? [],
        answer: answers[q.id],
      }));
      const r = await submitAttempt(attemptId, responses, seconds);
      setResult(r);
      if (r.passed) await onCompleted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Answered = has a non-empty value (array with items, or a truthy scalar).
  const isAnswered = (qid: any) => {
    const v = answers[qid];
    if (Array.isArray(v)) return v.length > 0;
    return v != null && v !== '';
  };
  const answeredCount = questions.filter(q => isAnswered(q.id)).length;
  const currentAnswered = current ? isAnswered(current.id) : false;

  // Restart the whole quiz from the results screen.
  const retake = () => {
    setResult(null); setQi(0); setAnswers({}); setError('');
    setLoadAttempt(n => n + 1);
  };

  // ── Render — fits inline in the chat column, height 100% of parent. ──
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', width: '100%',
      background: 'var(--surface)',
      animation: 'quizPanelSlideIn 320ms cubic-bezier(0.34, 1.2, 0.64, 1)',
    }}>
      {/* Header strip — badge + mono label, matching the tutor-chat header. */}
      <div style={{
        padding: '12px 18px', borderBottom: '1px solid var(--border)',
        background: 'var(--card)', flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ width: 30, height: 30, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--primary)', color: '#fff' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
          </span>
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
            Quiz{result ? ' · Results' : current ? ` · Q${qi + 1} of ${total}` : ''}
          </span>
        </div>
        <button onClick={onClose} disabled={submitting} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          background: 'var(--card)', border: '1px solid var(--border-md)',
          color: 'var(--muted)', fontSize: 12, fontWeight: 600,
          padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
          Back to tutor
        </button>
      </div>

      {/* Content — scrolls if tall */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {result ? (
          // After a PASS, "Claim your badge" → onPassExit (exits the whole
          // training session back to the My Training list). After a FAIL,
          // "Review & try again" → onClose (returns to the tutor chat so
          // the user can revisit the video and retry).
          <CelebrationScreen
            result={result}
            passingScore={passingScore}
            onClose={result.passed ? onPassExit : async () => onClose()}
            onRetake={retake}
          />
        ) : loadState === 'error' ? (
          // startAttempt threw (e.g. video not watched yet, network blip).
          // Surface the real message + a retry so the user isn't stuck.
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 42, marginBottom: 12 }}>⚠️</div>
            <div style={{ color: 'var(--accent-red)', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
              Couldn't start the quiz
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20, lineHeight: 1.55 }}>
              {error || 'Something went wrong.'}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setLoadAttempt(n => n + 1)}
                style={{
                  background: 'var(--primary)', color: 'var(--on-primary)',
                  border: 'none', borderRadius: 8, padding: '8px 20px',
                  fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
                }}
              >Retry</button>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent', color: 'var(--muted)',
                  border: '1px solid var(--border-md)', borderRadius: 8,
                  padding: '8px 20px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 13,
                }}
              >Back to tutor</button>
            </div>
          </div>
        ) : loadState === 'ready' && questions.length === 0 ? (
          // Module has zero questions — not the user's fault. Tell them
          // clearly and offer a way back instead of hanging on "Loading...".
          <div style={{ padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 42, marginBottom: 12 }}>📭</div>
            <div style={{ color: 'var(--fg)', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
              No quiz questions on this module yet
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 20, lineHeight: 1.55, maxWidth: 320, margin: '0 auto 20px' }}>
              Your admin hasn't added questions to this training module. Ask them to add a few, then come back.
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'var(--primary)', color: 'var(--on-primary)',
                border: 'none', borderRadius: 8, padding: '8px 20px',
                fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13,
              }}
            >Back to tutor</button>
          </div>
        ) : !current ? (
          // loadState === 'loading' — show a real spinner instead of dead text.
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--muted)' }}>
            <div style={{
              display: 'inline-block', width: 28, height: 28,
              border: '3px solid var(--faint)', borderTopColor: 'var(--primary)',
              borderRadius: '50%', animation: 'quizSpin 0.75s linear infinite',
              marginBottom: 12,
            }} />
            <div style={{ fontSize: 13 }}>Loading questions…</div>
            <style>{`@keyframes quizSpin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <>
            {/* Progress bar */}
            <div style={{ padding: '16px 20px 0' }}>
              <div style={{
                height: 6, background: 'var(--faint)', borderRadius: 999,
                overflow: 'hidden', marginBottom: 18,
              }}>
                <div style={{
                  height: '100%', width: `${progressPct}%`,
                  background: 'var(--primary)',
                  borderRadius: 999, transition: 'width 320ms ease',
                }} />
              </div>
            </div>

            {/* Question */}
            <div key={qi} style={{
              padding: '0 20px 20px',
              animation: 'quizQuestionSlide 240ms ease-out',
            }}>
              <div style={{
                fontSize: 17, fontWeight: 700, color: 'var(--fg)',
                fontFamily: 'var(--font-heading)',
                lineHeight: 1.4, marginBottom: 16,
              }}>
                {current.questionText}
              </div>

              {/* Type hint */}
              {current.questionType !== 'single_choice' && (
                <div style={{
                  fontSize: 10, color: 'var(--primary)', fontWeight: 700,
                  marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6,
                }}>
                  {current.questionType === 'multiple_choice' && 'Select all that apply'}
                  {current.questionType === 'drag_order' && 'Arrange in the correct order'}
                  {current.questionType === 'drag_match' && 'Match each item on the left to the right'}
                  {current.questionType === 'short_answer' && 'Type your answer'}
                </div>
              )}

              <QuestionRenderer
                question={current}
                value={answers[current.id]}
                onChange={(val) => { setAnswers(prev => ({ ...prev, [current.id]: val })); setError(''); }}
                disabled={false}
              />

              {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 12 }}>{error}</div>}
            </div>
          </>
        )}
      </div>

      {/* Pinned footer — answered counter + Next / Submit */}
      {!result && current && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          padding: '12px 20px', borderTop: '1px solid var(--border)', background: 'var(--card)', flexShrink: 0,
        }}>
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 11.5, fontWeight: 600, color: 'var(--muted)' }}>
            <span style={{ color: answeredCount > 0 ? 'var(--primary)' : 'var(--muted)' }}>{answeredCount}</span> of {total} answered
          </span>
          <button
            onClick={nextQuestion}
            disabled={submitting || !currentAnswered}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: submitting || !currentAnswered ? 'var(--faint)' : 'var(--primary)',
              color: submitting || !currentAnswered ? 'var(--muted)' : 'var(--on-primary)',
              border: 'none', borderRadius: 10, padding: '10px 20px',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: submitting ? 'wait' : !currentAnswered ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Scoring…' : isLast ? 'Submit quiz' : 'Next'}
            {!submitting && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>}
          </button>
        </div>
      )}
      <style>{`
        @keyframes quizPanelSlideIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes quizQuestionSlide {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ── Question renderers per type ─────────────────────────────────────────

function QuestionRenderer({ question, value, onChange, disabled }: {
  question: TrainingQuestion;
  value: any;
  onChange: (val: any) => void;
  disabled: boolean;
}) {
  switch (question.questionType) {
    case 'single_choice': return <ChoiceRenderer question={question} value={value} onChange={onChange} disabled={disabled} multi={false} />;
    case 'multiple_choice': return <ChoiceRenderer question={question} value={value} onChange={onChange} disabled={disabled} multi={true} />;
    case 'drag_order': return <OrderRenderer question={question} value={value} onChange={onChange} disabled={disabled} />;
    case 'drag_match': return <MatchRenderer question={question} value={value} onChange={onChange} disabled={disabled} />;
    case 'short_answer': return <ShortAnswerRenderer value={value} onChange={onChange} disabled={disabled} />;
    default: return <ChoiceRenderer question={question} value={value} onChange={onChange} disabled={disabled} multi={false} />;
  }
}

// Single / Multiple choice
function ChoiceRenderer({ question, value, onChange, disabled, multi }: {
  question: TrainingQuestion; value: any; onChange: (v: any) => void; disabled: boolean; multi: boolean;
}) {
  const selected: string[] = Array.isArray(value) ? value : (value ? [value] : []);

  function toggle(optValue: string) {
    if (disabled) return;
    if (multi) {
      const next = selected.includes(optValue) ? selected.filter(v => v !== optValue) : [...selected, optValue];
      onChange(next);
    } else {
      onChange([optValue]);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {question.options.map(opt => {
        const isSelected = selected.includes(opt.value);
        return (
          <button key={opt.value} onClick={() => toggle(opt.value)} disabled={disabled} className="quiz-option" style={{
            display: 'flex', alignItems: 'center', gap: 14, width: '100%',
            padding: '16px 18px', textAlign: 'left',
            background: isSelected ? 'color-mix(in srgb, var(--primary) 8%, var(--card))' : 'var(--card)',
            border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border-md)'}`,
            borderRadius: 14, cursor: disabled ? 'default' : 'pointer',
            fontSize: 14.5, color: 'var(--fg)', fontFamily: 'inherit',
            transition: 'border-color .12s, background .12s',
          }}>
            <span style={{
              width: 22, height: 22, borderRadius: multi ? 6 : '50%', flexShrink: 0,
              border: `2px solid ${isSelected ? 'var(--primary)' : 'var(--border-md)'}`,
              background: isSelected && multi ? 'var(--primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isSelected && (multi
                ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                : <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)' }} />)}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>{opt.text}</span>
          </button>
        );
      })}
    </div>
  );
}

// Drag & Order — click ↑/↓ arrows to reorder
function OrderRenderer({ question, value, onChange, disabled }: {
  question: TrainingQuestion; value: any; onChange: (v: any) => void; disabled: boolean;
}) {
  const items: string[] = Array.isArray(value) && value.length ? value : question.options.map(o => o.value);
  const textMap: Record<string, string> = {};
  question.options.forEach(o => { textMap[o.value] = o.text; });

  function move(idx: number, dir: -1 | 1) {
    if (disabled) return;
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((v, i) => (
        <div key={v} style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px',
          background: 'var(--surface)', border: '1px solid var(--border-md)',
          borderRadius: 10, fontSize: 14, color: 'var(--fg)',
        }}>
          <span style={{
            width: 24, height: 24, borderRadius: '50%',
            background: 'var(--primary-mid)', color: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
          }}>{i + 1}</span>
          <span style={{ flex: 1 }}>{textMap[v] || v}</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button onClick={() => move(i, -1)} disabled={disabled || i === 0} style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 4, padding: '1px 6px', cursor: disabled || i === 0 ? 'default' : 'pointer',
              color: 'var(--muted)', fontSize: 10, lineHeight: 1, opacity: i === 0 ? 0.3 : 1,
            }}>▲</button>
            <button onClick={() => move(i, 1)} disabled={disabled || i === items.length - 1} style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 4, padding: '1px 6px', cursor: disabled || i === items.length - 1 ? 'default' : 'pointer',
              color: 'var(--muted)', fontSize: 10, lineHeight: 1, opacity: i === items.length - 1 ? 0.3 : 1,
            }}>▼</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// Drag & Match — click left then right to create pairs
function MatchRenderer({ question, value, onChange, disabled }: {
  question: TrainingQuestion; value: any; onChange: (v: any) => void; disabled: boolean;
}) {
  const pairs: Record<string, string> = typeof value === 'object' && !Array.isArray(value) && value ? value : {};
  const targets = question.matchOptions || [];
  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);

  function pick(leftVal: string) {
    if (disabled) return;
    setSelectedLeft(leftVal);
  }
  function match(rightVal: string) {
    if (disabled || !selectedLeft) return;
    const next = { ...pairs, [selectedLeft]: rightVal };
    onChange(next);
    setSelectedLeft(null);
  }
  function clear(leftVal: string) {
    if (disabled) return;
    const next = { ...pairs };
    delete next[leftVal];
    onChange(next);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 10 }}>
      {/* Left column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Items</div>
        {question.options.map(opt => {
          const isMatched = !!pairs[opt.value];
          const isActive = selectedLeft === opt.value;
          return (
            <button key={opt.value} onClick={() => isMatched ? clear(opt.value) : pick(opt.value)} disabled={disabled} style={{
              textAlign: 'left', padding: '10px 12px',
              background: isActive ? 'var(--primary-mid)' : isMatched ? 'rgba(74,222,128,0.10)' : 'var(--surface)',
              border: `2px solid ${isActive ? 'var(--primary)' : isMatched ? 'rgba(74,222,128,0.30)' : 'var(--border)'}`,
              borderRadius: 8, fontSize: 13, color: 'var(--fg)', cursor: disabled ? 'default' : 'pointer',
              fontFamily: 'inherit',
            }}>
              {opt.text}
              {isMatched && <span style={{ marginLeft: 6, fontSize: 10, color: '#4ade80' }}>✓</span>}
            </button>
          );
        })}
      </div>

      {/* Arrow column */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 20 }}>
        →
      </div>

      {/* Right column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>Match to</div>
        {targets.map(opt => {
          const matched = Object.entries(pairs).find(([, rv]) => rv === opt.value);
          return (
            <button key={opt.value} onClick={() => match(opt.value)} disabled={disabled || !selectedLeft || !!matched} style={{
              textAlign: 'left', padding: '10px 12px',
              background: matched ? 'rgba(74,222,128,0.10)' : selectedLeft ? 'var(--primary-dim)' : 'var(--surface)',
              border: `2px solid ${matched ? 'rgba(74,222,128,0.30)' : selectedLeft ? 'var(--primary-border)' : 'var(--border)'}`,
              borderRadius: 8, fontSize: 13, color: 'var(--fg)',
              cursor: (disabled || !selectedLeft || !!matched) ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: matched ? 0.6 : 1,
            }}>
              {opt.text}
              {matched && <span style={{ marginLeft: 6, fontSize: 10, color: '#4ade80' }}>← {question.options.find(o => o.value === matched[0])?.text}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Short answer
function ShortAnswerRenderer({ value, onChange, disabled }: {
  value: any; onChange: (v: any) => void; disabled: boolean;
}) {
  return (
    <textarea
      value={typeof value === 'string' ? value : ''}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      placeholder="Type your answer here…"
      rows={3}
      style={{
        width: '100%', padding: '14px 16px',
        background: 'var(--surface)', color: 'var(--fg)',
        border: '2px solid var(--border-md)', borderRadius: 10,
        fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
        outline: 'none',
      }}
    />
  );
}

// ── Celebration / Results screen ────────────────────────────────────────

// Confetti color palette
const CONFETTI_COLORS = ['#0067FE', '#4ade80', '#fbbf24', '#f87171', '#60a5fa', '#fb923c', '#ec4899'];

function CelebrationScreen({ result, passingScore, onClose, onRetake }: {
  result: AttemptResult;
  passingScore: number;
  /** Pass = exits the session; Fail = returns to the tutor chat. The
   *  caller decides the correct target based on result.passed. */
  onClose: () => void | Promise<void>;
  /** Restart the quiz from scratch (secondary action). */
  onRetake: () => void;
}) {
  const passed = result.passed;
  const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

  return (
    <div style={{ padding: '36px 28px 28px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>

      {/* ── Confetti particles (passed only) — CSS-only, no library ── */}
      {passed && Array.from({ length: 40 }).map((_, i) => (
        <div key={i} className="confetti-particle" style={{
          left: `${Math.random() * 100}%`,
          width: `${6 + Math.random() * 8}px`,
          height: `${6 + Math.random() * 8}px`,
          background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          animationDelay: `${Math.random() * 1.5}s`,
          animationDuration: `${2 + Math.random() * 2}s`,
          opacity: 0.9,
        }} />
      ))}

      {/* ── Score circle — pops in with scale animation ── */}
      <div style={{
        animation: 'scoreCountUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        animationDelay: '0.2s',
      }}>
        <div style={{
          width: 118, height: 118, borderRadius: '50%',
          background: passed ? 'color-mix(in srgb, var(--success) 6%, transparent)' : 'color-mix(in srgb, var(--accent-red) 8%, transparent)',
          border: `7px solid ${passed ? 'var(--success)' : 'var(--accent-red)'}`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 30, fontWeight: 800, letterSpacing: '-0.01em',
          color: passed ? 'var(--success)' : 'var(--accent-red)',
          fontFamily: 'var(--font-heading)',
          marginBottom: 20,
        }}>
          {result.score}%
        </div>
      </div>

      {/* ── Title — staggered fade-in ── */}
      <div style={{ animation: 'badgeSlideUp 0.5s ease both', animationDelay: '0.5s' }}>
        <h2 style={{
          fontFamily: 'var(--font-heading)', fontSize: 30, fontWeight: 700,
          color: 'var(--fg)', marginBottom: 8,
        }}>
          {passed ? 'Congratulations!' : 'Nice try!'}
        </h2>
        <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto 6px' }}>
          {passed
            ? "You've passed the quiz and earned your Crisis Management badge — you're now certified."
            : `You scored ${result.score}% — you need ${passingScore}% to pass. Review the material and give it another go. You've got this!`}
        </p>
        <p style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: 12, color: 'var(--subtle)', marginBottom: 24 }}>
          {result.correct} of {result.total} correct · pass mark {passingScore}%
        </p>
      </div>

      {/* ── Badge card (passed) ── */}
      {passed && (
        <div style={{
          animation: 'badgeSlideUp 0.6s ease both', animationDelay: '0.8s',
          marginBottom: 28, display: 'block',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            background: '#0153D4',
            color: '#fff', padding: '18px 22px', borderRadius: 16,
            boxShadow: '0 14px 34px -14px rgba(0,103,254,0.45)',
          }}>
            <span style={{
              width: 52, height: 52, borderRadius: 14, flexShrink: 0, display: 'grid', placeItems: 'center',
              background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.22)',
            }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>
            </span>
            <div style={{ textAlign: 'left', minWidth: 0 }}>
              <div style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em', marginBottom: 4,
              }}>
                Achievement Unlocked
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em' }}>
                Crisis Management Certified
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New certificate unlocked (milestone 3/5/10/15+) ── */}
      {passed && result.newCertificate && (
        <NewCertificateUnlocked newCert={result.newCertificate} />
      )}

      {/* ── Per-question breakdown — staggered ── */}
      <div style={{
        textAlign: 'left', marginBottom: 22,
        animation: 'badgeSlideUp 0.4s ease both',
        animationDelay: passed ? '1.2s' : '0.6s',
      }}>
        <div style={{
          fontFamily: mono, fontSize: 10, fontWeight: 700, color: 'var(--subtle)',
          textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 12,
        }}>
          Question breakdown
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {result.results.map((r, i) => {
            // Normalise the expected/given payloads for display so users can
            // see exactly what they answered vs what the scorer wanted. This
            // also makes bad scoring (e.g. drag-match stored as single_choice
            // with an object-shaped correctAnswers) obvious at a glance.
            const fmt = (v: any): string => {
              if (v === null || v === undefined) return '—';
              if (typeof v === 'string') return v || '—';
              if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : '—';
              if (typeof v === 'object') {
                return Object.entries(v).map(([k, val]) => `${k} → ${val}`).join(' · ') || '—';
              }
              return String(v);
            };
            const given    = fmt((r as any).given);
            const expected = fmt((r as any).expected);
            return (
              <div key={r.questionId} style={{
                padding: '14px 16px', borderRadius: 12,
                background: r.correct ? 'color-mix(in srgb, var(--success) 7%, var(--card))' : 'color-mix(in srgb, var(--accent-red) 7%, var(--card))',
                border: `1px solid ${r.correct ? 'color-mix(in srgb, var(--success) 26%, transparent)' : 'color-mix(in srgb, var(--accent-red) 26%, transparent)'}`,
                fontSize: 13,
                animation: 'badgeSlideUp 0.3s ease both',
                animationDelay: `${(passed ? 1.4 : 0.8) + i * 0.1}s`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    background: r.correct ? 'var(--success)' : 'var(--accent-red)',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {r.correct
                      ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>}
                  </span>
                  <span style={{ fontWeight: 700, color: 'var(--fg)', fontSize: 14 }}>Q{i + 1}</span>
                </div>
                <div style={{ marginTop: 9, marginLeft: 34, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 12.5, alignItems: 'baseline' }}>
                  <span style={{ fontFamily: mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--subtle)' }}>Your answer</span>
                  <span style={{ color: r.correct ? 'var(--fg)' : 'var(--accent-red)', fontWeight: 600, wordBreak: 'break-word' }}>{given}</span>
                  {!r.correct && (
                    <>
                      <span style={{ fontFamily: mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--subtle)' }}>Correct</span>
                      <span style={{ color: 'var(--success)', fontWeight: 600, wordBreak: 'break-word' }}>{expected}</span>
                    </>
                  )}
                  {r.explanation && (
                    <>
                      <span style={{ fontFamily: mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--subtle)' }}>Why</span>
                      <span style={{ color: 'var(--muted)', lineHeight: 1.5 }}>{r.explanation}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── CTA button ── */}
      <div style={{
        animation: 'badgeSlideUp 0.4s ease both',
        animationDelay: passed ? '1.8s' : '1s',
      }}>
        <button onClick={onClose} style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%',
          background: passed ? 'var(--primary)' : 'var(--card)',
          color: passed ? '#fff' : 'var(--fg)',
          border: passed ? 'none' : '1px solid var(--border-md)',
          borderRadius: 12, padding: '15px 24px',
          fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          boxShadow: passed ? '0 8px 22px -8px color-mix(in srgb, var(--primary) 60%, transparent)' : 'none',
        }}>
          {passed && <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>}
          {passed ? 'Claim your badge' : 'Review & try again'}
        </button>
        <button onClick={onRetake} style={{
          display: 'block', margin: '14px auto 0', background: 'transparent', border: 'none',
          color: 'var(--muted)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}>
          Retake the quiz
        </button>
      </div>
    </div>
  );
}

// ── New certificate unlocked banner (shown only on the completion screen) ──
// Sits between the badge card and the per-question breakdown. Animates in
// ~1.2s after the confetti so it doesn't compete with the badge reveal.
function NewCertificateUnlocked({ newCert }: {
  newCert: { id: number; milestone: number; certificateCode: string };
}) {
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState('');

  async function handleView() {
    setOpening(true); setErr('');
    try {
      await openCertificate(newCert.id);
    } catch (e: any) {
      setErr(e.message || 'Could not open certificate');
    } finally {
      setOpening(false);
    }
  }

  return (
    <div style={{
      animation: 'badgeSlideUp 0.6s ease both, pulseGlow 2.4s ease-in-out infinite',
      animationDelay: '1.2s, 2.2s',
      display: 'inline-block',
      marginBottom: 28,
    }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 18,
        padding: '20px 26px',
        borderRadius: 18,
        color: '#fff',
        background: '#0153D4',
        boxShadow: '0 18px 44px rgba(30,58,138,0.38)',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'var(--cs-amber)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, flexShrink: 0,
          boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.32), 0 4px 10px rgba(0,0,0,0.2)',
        }}>
          🏆
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            opacity: 0.8, letterSpacing: 1.5, marginBottom: 3,
          }}>
            New Certificate
          </div>
          <div style={{
            fontSize: 17, fontWeight: 700,
            fontFamily: 'var(--font-heading)', marginBottom: 2,
          }}>
            {newCert.milestone} Modules Completed!
          </div>
          <div style={{ fontSize: 11, opacity: 0.78 }}>
            Code {newCert.certificateCode}
          </div>
        </div>
        <button onClick={handleView} disabled={opening} style={{
          background: 'rgba(255,255,255,0.16)',
          border: '1px solid rgba(255,255,255,0.35)',
          color: '#fff', padding: '9px 16px', borderRadius: 10,
          fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
          cursor: opening ? 'wait' : 'pointer',
          letterSpacing: 0.4,
          backdropFilter: 'blur(8px)',
          flexShrink: 0,
        }}>
          {opening ? 'Opening…' : 'View & print →'}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: 11, color: '#fecaca', marginTop: 6 }}>⚠ {err}</div>
      )}
    </div>
  );
}

